import {useEffect, useState} from "react";
import {Plus, Save, GripVertical, Check, X, Ban, AlertTriangle, Mail} from "lucide-react";
import {apiGet, apiPost, apiPut} from "@/lib/api";
import axios from "axios";
import {API} from "@/lib/api";
import {BlogAdminPanel} from "@/pages/Blog";
import {LeadsPanel, PlansPanel, ProfilesPanel, EventApprovalsPanel, ContactPanel} from "@/pages/AdminPanels";

export default function Admin({notify}) {
  const [kind, setKind] = useState("listings");
  const [title, setTitle] = useState("");
  const [order, setOrder] = useState(["Home","Search","Events around","Profile","Bazaar","Samaj","Ask My Community","Your Personal Concierge","Panchang","Culture"]);
  const [pending, setPending] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportFilter, setReportFilter] = useState("pending");
  const [reminderStatus, setReminderStatus] = useState(null);

  useEffect(() => {
    apiGet("/communities/pending").then(setPending).catch(() => setPending([]));
    apiGet("/brand-suggestions").then(setSuggestions).catch(() => setSuggestions([]));
    apiGet("/admin/reminders/status").then(setReminderStatus).catch(() => {});
  }, []);

  useEffect(() => {
    apiGet("/admin/reports", {status: reportFilter === "all" ? "" : reportFilter}).then(setReports).catch(() => setReports([]));
  }, [reportFilter]);

  const add = async () => {
    try {
      await apiPost(`/admin/content/${kind}`, {title, description: "New managed item", status: "Published", section: "Main"});
      setTitle(""); notify("Content item saved");
    } catch { notify("Admin access is required to save content"); }
  };

  const approve = async (id) => {
    try { await apiPost(`/admin/communities/${id}/approve`, {}); setPending(p => p.filter(c => c.community_id !== id)); notify("Samaj approved"); }
    catch { notify("Admin access required"); }
  };
  const reject = async (id) => {
    try { await apiPost(`/admin/communities/${id}/reject`, {}); setPending(p => p.filter(c => c.community_id !== id)); notify("Samaj rejected"); }
    catch { notify("Admin access required"); }
  };

  const saveOrder = async () => {
    try { await axios.post(`${API}/admin/sections/reorder`, {order}, {withCredentials: true}); notify("Navigation order saved"); }
    catch { notify("Admin access required to save order"); }
  };

  return (
    <div data-testid="admin-page">
      <div className="page-title">
        <div>
          <div className="eyebrow">CONTROL ROOM</div>
          <h1>Shape the <em>experience.</em></h1>
          <p>Approve new samaj, work the Your Personal Concierge call-back inbox, manage demo profiles and reorder sections.</p>
        </div>
        <button data-testid="admin-save" className="coral-button" onClick={saveOrder}><Save size={16}/> Save navigation order</button>
      </div>

      <div className="admin-grid">
        <section className="admin-panel">
          <div className="admin-panel-head">
            <div>
              <div className="eyebrow">CONTENT LIBRARY</div>
              <h2>Edit content</h2>
            </div>
            <select data-testid="admin-kind" value={kind} onChange={e => setKind(e.target.value)}>
              <option>listings</option><option>samaj</option><option>blogs</option><option>calendar</option><option>reports</option>
            </select>
          </div>
          <div className="admin-add">
            <input data-testid="admin-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Add a title..."/>
            <button data-testid="admin-add" className="coral-button" onClick={add}><Plus size={15}/> Add</button>
          </div>
          <p className="admin-help">Content writes are protected for admin accounts.</p>
        </section>

        <section className="admin-panel">
          <div className="eyebrow">NAVIGATION LABELS</div>
          <h2>Drag &amp; drop sections</h2>
          <div className="drag-list">
            {order.map((x, i) => (
              <div draggable
                onDragStart={e => e.dataTransfer.setData("i", i)}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { const a = Number(e.dataTransfer.getData("i")); const copy = [...order]; const [m] = copy.splice(a, 1); copy.splice(i, 0, m); setOrder(copy); }}
                className="drag-row" key={x} data-testid={`drag-${x.replace(/\s+/g,"-")}`}>
                <GripVertical size={17}/>
                <span>{x}</span>
                <span className="drag-number">0{i+1}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="admin-panel wide" data-testid="admin-pending-circles">
          <div className="admin-panel-head">
            <div>
              <div className="eyebrow">SAMAJ APPROVALS</div>
              <h2>Pending samaj ({pending.length})</h2>
            </div>
          </div>
          {pending.length === 0 ? (
            <p className="admin-help">No pending samaj. New submissions will appear here.</p>
          ) : pending.map(c => (
            <div key={c.community_id} className="admin-row">
              <div>
                <strong>{c.name}</strong>
                <small>{c.topic} · {c.type} · by {c.created_by_name}</small>
                <p className="admin-row-desc">{c.description}</p>
              </div>
              <button data-testid={`approve-${c.community_id}`} className="coral-button sm" onClick={() => approve(c.community_id)}><Check size={13}/> Approve</button>
              <button data-testid={`reject-${c.community_id}`} className="outline-button sm" onClick={() => reject(c.community_id)}><X size={13}/> Reject</button>
            </div>
          ))}
        </section>

        <LeadsPanel notify={notify}/>
        <EventApprovalsPanel notify={notify}/>
        <ContactPanel notify={notify}/>
        <PlansPanel notify={notify}/>
        <ProfilesPanel notify={notify}/>

        <section className="admin-panel wide" data-testid="admin-reports">
          <div className="admin-panel-head">
            <div>
              <div className="eyebrow">REPORT INBOX</div>
              <h2>Flagged content ({reports.length})</h2>
            </div>
            <select data-testid="report-filter" value={reportFilter} onChange={e => setReportFilter(e.target.value)}>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
              <option value="all">All</option>
            </select>
          </div>
          {reports.length === 0 ? (
            <p className="admin-help">No {reportFilter} reports.</p>
          ) : reports.map(r => (
            <div key={r.report_id} className="report-row" data-testid={`report-${r.report_id}`}>
              <span className="ct-pill">{r.content_type}</span>
              <div>
                <strong>{r.reason || "No reason given"}</strong>
                <div className="meta">Reported by {r.reporter_name} · <span className={`status-${r.status}`}>{r.status}</span>{r.action_taken && ` · action: ${r.action_taken}`} · {new Date(r.created_at).toLocaleString()}</div>
                {r.preview && r.preview.found ? (
                  <div className="report-preview" data-testid={`report-preview-${r.report_id}`}>
                    <small className="meta">BY {r.preview.author || r.preview.name || "Unknown"}</small>
                    <p>{(r.preview.text || r.preview.body || r.preview.name || "No text content") .substring(0,240)}{(r.preview.text || r.preview.body || "").length > 240 ? "…" : ""}</p>
                  </div>
                ) : r.preview ? (
                  <small className="meta">⚠ {r.preview.note}</small>
                ) : (
                  <small className="meta">Content id: {r.content_id}</small>
                )}
              </div>
              {r.status === "pending" ? (
                <div className="report-actions">
                  <button data-testid={`report-ignore-${r.report_id}`} className="outline-button sm" onClick={async () => { try { await apiPost(`/admin/reports/${r.report_id}/resolve`, {action:"ignore"}); setReports(x => x.filter(y => y.report_id !== r.report_id)); notify("Report ignored"); } catch { notify("Admin only"); } }}>
                    <Check size={13}/> Ignore
                  </button>
                  <button data-testid={`report-warn-${r.report_id}`} className="outline-button sm" onClick={async () => { try { await apiPost(`/admin/reports/${r.report_id}/resolve`, {action:"warn"}); setReports(x => x.filter(y => y.report_id !== r.report_id)); notify("User warned"); } catch { notify("Admin only"); } }}>
                    <AlertTriangle size={13}/> Warn
                  </button>
                  <button data-testid={`report-ban-${r.report_id}`} className="ban-btn" onClick={async () => { if (!window.confirm("Ban the author? They will be signed out immediately.")) return; try { const res = await apiPost(`/admin/reports/${r.report_id}/resolve`, {action:"ban"}); setReports(x => x.filter(y => y.report_id !== r.report_id)); notify(res.banned_user_id ? `Banned ${res.banned_user_id}` : "Report resolved"); } catch { notify("Admin only"); } }}>
                    <Ban size={13}/> Ban
                  </button>
                </div>
              ) : (
                <span className="meta">Resolved</span>
              )}
            </div>
          ))}
        </section>

        <section className="admin-panel wide" data-testid="admin-reminders">
          <div className="admin-panel-head">
            <div>
              <div className="eyebrow">EMAIL REMINDERS</div>
              <h2>Event &amp; festival emails</h2>
            </div>
            <span className="pill" style={{background: reminderStatus?.email_configured ? "#fff0e9" : "#f3eee4", color: reminderStatus?.email_configured ? "#c95433" : "#5a6b73"}}>
              <Mail size={11} style={{marginRight:4}}/> {reminderStatus?.email_configured ? "EMAIL ON" : "IN-APP ONLY"}
            </span>
          </div>
          <p className="admin-help">
            {reminderStatus?.email_configured
              ? `Day-before event reminders go out at 09:00 IST and festival-morning nudges at 06:30 IST from ${reminderStatus.sender}. ${reminderStatus.total_runs} event runs so far.`
              : "RESEND_API_KEY is not set yet. The scheduler is live and will flip to email the moment the key is added — meanwhile upcoming events still show on Home."}
          </p>
          {reminderStatus?.recent?.length > 0 && (
            <div style={{marginTop:12}}>
              {reminderStatus.recent.slice(0, 3).map((r, i) => (
                <small key={i} className="meta" style={{display:"block"}}>Run {new Date(r.created_at).toLocaleString()} · {r.sent_to.length} recipients</small>
              ))}
            </div>
          )}
        </section>

        <BlogAdminPanel notify={notify}/>

        <section className="admin-panel wide" data-testid="admin-suggestions">
          <div className="eyebrow">BRAND SUGGESTIONS</div>
          <h2>Submitted by members ({suggestions.length})</h2>
          {suggestions.length === 0 ? (
            <p className="admin-help">No brand suggestions yet.</p>
          ) : suggestions.map(s => (
            <div key={s.suggestion_id} className="admin-row">
              <div>
                <strong>{s.name}</strong>
                <small>{s.category} · {s.location}</small>
                <p className="admin-row-desc">{s.reason}</p>
                {s.link && <small><a href={s.link} target="_blank" rel="noreferrer">{s.link}</a></small>}
              </div>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
